import { createElement } from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { useTranslation } from 'react-i18next';
import { SEO_CONFIG } from '@/shared/seo/seo.config';

interface PageSEOOptions {
  title: string;
  description: string;
  canonicalPath: string;
  noSuffix?: boolean;
  robots?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown>;
}

export function usePageSEO(options: PageSEOOptions) {
  const { i18n } = useTranslation();
  const lang = i18n.language || SEO_CONFIG.defaultLanguage;

  const {
    title,
    description,
    canonicalPath,
    noSuffix = false,
    robots = 'index, follow',
    ogType = 'website',
    jsonLd
  } = options;

  const fullTitle = noSuffix ? title : `${title} | ${SEO_CONFIG.siteName}`;
  const canonicalUrl = `${SEO_CONFIG.siteUrl}${canonicalPath}`;
  const imageUrl = `${SEO_CONFIG.siteUrl}${SEO_CONFIG.defaultImage}`;

  const hreflangLinks = SEO_CONFIG.supportedLanguages.map((lng) =>
    createElement('link', {
      key: `hreflang-${lng}`,
      rel: 'alternate',
      hrefLang: lng,
      href: canonicalUrl
    })
  );

  return createElement(
    Helmet,
    null,
    createElement('html', { lang }),
    createElement('title', null, fullTitle),
    createElement('meta', { name: 'description', content: description }),
    createElement('meta', { name: 'robots', content: robots }),
    createElement('link', { rel: 'canonical', href: canonicalUrl }),

    // Open Graph
    createElement('meta', { property: 'og:type', content: ogType }),
    createElement('meta', { property: 'og:site_name', content: SEO_CONFIG.siteName }),
    createElement('meta', { property: 'og:title', content: fullTitle }),
    createElement('meta', { property: 'og:description', content: description }),
    createElement('meta', { property: 'og:image', content: imageUrl }),
    createElement('meta', { property: 'og:url', content: canonicalUrl }),
    createElement('meta', { property: 'og:locale', content: lang }),

    // Twitter Card
    createElement('meta', { name: 'twitter:card', content: 'summary' }),
    createElement('meta', { name: 'twitter:title', content: fullTitle }),
    createElement('meta', { name: 'twitter:description', content: description }),
    createElement('meta', { name: 'twitter:image', content: imageUrl }),

    // Hreflang alternates
    ...hreflangLinks,
    createElement('link', {
      rel: 'alternate',
      hrefLang: 'x-default',
      href: canonicalUrl
    }),

    // JSON-LD structured data
    ...(jsonLd
      ? [
          createElement('script', {
            type: 'application/ld+json',
            dangerouslySetInnerHTML: { __html: JSON.stringify(jsonLd) }
          })
        ]
      : [])
  );
}
